import { createFileRoute } from '@tanstack/react-router'
import { ProductFrame } from '@/components/marketing/product-frame'
import { UptimeBarsPiece } from '@/components/marketing/ui-pieces'
import { FeaturePage } from '@/components/feature-page'
import { ExtraSection } from '@/components/feature-extra'

export const Route = createFileRoute('/_site/for/agencies')({
  component: AgenciesPage,
  head: () => ({
    meta: [
      { title: 'Website Monitoring for Agencies — Client Sites' },
      { name: 'description', content: 'Monitor every client site from one dashboard. White-label status pages, team roles, API automation. Pro from $3/mo.' },
      { property: 'og:title', content: 'Website Monitoring for Agencies — Monitor Client Sites | Uptrack' },
      { property: 'og:description', content: 'One dashboard for all client sites. White-label status pages, team roles, API automation. 50 monitors on Pro.' },
      { property: 'og:image', content: 'https://uptrack.app/og/for-agencies.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [{ rel: 'canonical', href: 'https://uptrack.app/for/agencies' }],
    scripts: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://uptrack.app' },
            { '@type': 'ListItem', position: 2, name: 'For Agencies', item: 'https://uptrack.app/for/agencies' },
          ],
        }),
      },
    ],
  }),
})

const steps = [
  { title: 'Onboard a new client', desc: 'Create monitors for their homepage, API, and key pages via the dashboard or API. Set up a status page on their subdomain.' },
  { title: 'Monitor around the clock', desc: 'Uptrack checks every endpoint every 30 seconds. Your team gets Slack alerts. The client sees their status page.' },
  { title: 'Respond before the client notices', desc: 'A client site returns a 500. You get alerted, fix it, and send the client a summary. They see the resolved incident on their status page.' },
  { title: 'Report monthly uptime', desc: 'Pull uptime percentages and response time data for client reports. Demonstrate the value of your retainer.' },
]

function AgenciesPage() {
  return (
    <FeaturePage
      section="Agency website monitoring"
      eyebrow="For · Agency website monitoring"
      title="Monitor every client site from one dashboard."
      intro="1,000 monitors on Business — enough for 100+ client websites. White-label status pages, custom email sender, team members with roles, and a full API for automation."
      featuresHeading="Built for agencies managing client sites"
      features={[
        { title: '1,000 monitors on Business', desc: 'Monitor 5-10 endpoints per client across 100+ websites. One plan covers your entire portfolio.' },
        { title: 'White-label status pages', desc: 'Give each client a branded status page on their own domain. Show them you are watching.' },
        { title: 'Custom email sender', desc: 'Alert notifications come from your domain, not ours. Maintain your brand in every client inbox.' },
        { title: 'Team members with roles', desc: 'Add developers, account managers, and clients with appropriate access levels per project.' },
        { title: 'API for automation', desc: 'Onboard new clients programmatically. Create monitors, status pages, and alert channels via API.' },
        { title: 'Organized by project', desc: 'Group monitors by client or project. Keep your dashboard clean as your roster grows.' },
      ]}
      cta={{
        heading: 'Start with 50 monitors on Pro',
        body: 'Try it with a few client sites. Upgrade to Business when you need 1,000 monitors and white-label status pages.',
      }}
      visual={
        <ProductFrame url="uptrack.app/dashboard">
          <UptimeBarsPiece />
        </ProductFrame>
      }
    >
      <ExtraSection title="How agencies use Uptrack" intro="A typical agency workflow managing 50 client websites.">
        <ol className="divide-y divide-border border-y border-border">
          {steps.map((s, i) => (
            <li key={s.title} className="flex gap-4 py-5">
              <span className="w-5 shrink-0 num text-sm text-muted-foreground">{i + 1}</span>
              <div>
                <h3 className="font-medium">{s.title}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{s.desc}</p>
              </div>
            </li>
          ))}
        </ol>
      </ExtraSection>
    </FeaturePage>
  )
}
